import { cn } from '@/utils/Cn'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { SpaceListColumn, SpaceListColumnSettings } from '@valuecase/common'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateHeader,
  EmptyStateTitle,
  IonIcon,
  ScrollArea,
  ScrollAreaGradient,
  Highlight,
} from '@valuecase/ui-components'
import {
  Switch,
  SwitchSize,
} from '@valuecase/ui-components/src/components/legacy/Form/Switch/Switch'
import { Input2 } from '@valuecase/ui-components/src/components/ui/input'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface DraggableColumnRowProps {
  label: string
  enabled: boolean
  onToggle: () => void
  listeners?: any
  attributes?: any
  isDragging?: boolean
  setNodeRef?: (el: HTMLElement | null) => void
  grabDisabled?: boolean
  secondaryLabel?: string
  searchTerm?: string
}

const DraggableColumnRow = ({
  label,
  enabled,
  onToggle,
  listeners,
  attributes,
  isDragging,
  setNodeRef,
  grabDisabled,
  secondaryLabel,
  searchTerm = '',
}: DraggableColumnRowProps) => (
  <div
    ref={setNodeRef}
    {...attributes}
    className={cn('flex items-center gap-2 p-2 justify-between', isDragging && 'opacity-50')}
  >
    <div className='flex items-center gap-2'>
      <span
        {...(!grabDisabled ? listeners : {})}
        className={cn(
          'cursor-grab flex items-center',
          grabDisabled && 'opacity-30 cursor-not-allowed',
        )}
      >
        <IonIcon name='c_draggable' className='text-grey-s3' />
      </span>
      <span className='text-sm font-medium text-grey-s6'>
        <Highlight searchTerm={searchTerm}>{label}</Highlight>
      </span>
      {secondaryLabel && <span className='text-xs text-grey-s4 ml-2'>{secondaryLabel}</span>}
    </div>
    <Switch size={SwitchSize.SMALL} checked={enabled} onChange={onToggle} />
  </div>
)

interface SortableColumnRowProps {
  id: SpaceListColumn
  label: string
  enabled: boolean
  onToggle: () => void
  secondaryLabel?: string
  searchTerm?: string
}

const SortableColumnRow = ({
  id,
  label,
  enabled,
  onToggle,
  secondaryLabel,
  searchTerm,
}: SortableColumnRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  return (
    <div style={style}>
      <DraggableColumnRow
        label={label}
        enabled={enabled}
        onToggle={onToggle}
        listeners={listeners}
        attributes={attributes}
        setNodeRef={setNodeRef}
        isDragging={isDragging}
        secondaryLabel={secondaryLabel}
        searchTerm={searchTerm}
      />
    </div>
  )
}

const columnLabels: Record<SpaceListColumn, string> = {
  score: 'Engagement (14d)',
  templateName: 'Template',
  mapStatus: 'Status',
  mapProgress: 'Progress',
  currentMilestone: 'Current milestone',
  lastCompletedMilestone: 'Last completed milestone',
  currentOpenTask: 'Current open task',
  currentOpenTaskScheduled: 'Task scheduled',
  nextDueDate: 'Next due date',
  lastCompletedTaskDate: 'Last completed task date',
  lastCompletedTask: 'Last completed task',
  createdAt: 'Created at',
  updatedAt: 'Last changed',
  ownerId: 'Owner',
  lastVisit: 'Last visit',
  visits: 'Visits',
  activitiesCount: 'Activities count',
  teams: 'Teams',
}

const columnSecondaryLabels: Partial<Record<SpaceListColumn, string>> = {
  mapStatus: 'Action plan',
  mapProgress: 'Action plan',
  currentMilestone: 'Action plan',
  lastCompletedMilestone: 'Action plan',
  currentOpenTask: 'Action plan',
  currentOpenTaskScheduled: 'Action plan',
  nextDueDate: 'Action plan',
  lastCompletedTaskDate: 'Action plan',
  lastCompletedTask: 'Action plan',
  score: undefined,
  activitiesCount: undefined,
  templateName: undefined,
  teams: undefined,
  ownerId: undefined,
  lastVisit: undefined,
  visits: undefined,
  createdAt: undefined,
  updatedAt: undefined,
}

export interface SpaceListColumnSettingsPopoverProps {
  viewSettings: SpaceListColumnSettings
  onUpdateViewSettings: (settings: SpaceListColumnSettings) => void
}

const SpaceListColumnSettingsPopover = ({
  viewSettings,
  onUpdateViewSettings,
}: SpaceListColumnSettingsPopoverProps) => {
  const [localOrder, setLocalOrder] = useState<SpaceListColumn[]>(viewSettings.order)
  const [search, setSearch] = useState('')
  const sensors = useSensors(useSensor(PointerSensor))

  useEffect(() => {
    setLocalOrder(viewSettings.order)
  }, [viewSettings.order])

  // Filter columns by search and visibility
  const filteredShown = useMemo(
    () =>
      localOrder.filter(
        (id) =>
          viewSettings.visible[id] && columnLabels[id].toLowerCase().includes(search.toLowerCase()),
      ),
    [localOrder, viewSettings.visible, search],
  )
  const filteredHidden = useMemo(
    () =>
      localOrder.filter(
        (id) =>
          !viewSettings.visible[id] &&
          columnLabels[id].toLowerCase().includes(search.toLowerCase()),
      ),
    [localOrder, viewSettings.visible, search],
  )

  // Optionally, add any columns not in localOrder that are hidden (for completeness)
  const extraHidden = useMemo(
    () =>
      (Object.keys(viewSettings.visible) as SpaceListColumn[]).filter(
        (id) =>
          !viewSettings.visible[id] &&
          !localOrder.includes(id) &&
          columnLabels[id].toLowerCase().includes(search.toLowerCase()),
      ),
    [viewSettings.visible, localOrder, search],
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (active.id !== over?.id) {
        const oldIndex = localOrder.indexOf(active.id as SpaceListColumn)
        const newIndex = localOrder.indexOf(over?.id as SpaceListColumn)
        const newOrder = arrayMove(localOrder, oldIndex, newIndex)
        setLocalOrder(newOrder)
        onUpdateViewSettings({ ...viewSettings, order: newOrder })
      }
    },
    [localOrder, onUpdateViewSettings, viewSettings],
  )

  const handleToggle = useCallback(
    (id: SpaceListColumn) => {
      const newVisible = {
        ...viewSettings.visible,
        [id]: !viewSettings.visible[id],
      }

      // If we're toggling the column on, move it to the end of the order
      let newOrder = [...localOrder]
      if (!viewSettings.visible[id]) {
        // Remove the column from its current position
        newOrder = newOrder.filter((columnId) => columnId !== id)
        // Add it to the end
        newOrder.push(id)
        setLocalOrder(newOrder)
      }

      onUpdateViewSettings({ ...viewSettings, visible: newVisible, order: newOrder })
    },
    [onUpdateViewSettings, viewSettings, localOrder],
  )

  const viewPortRef = useRef<HTMLDivElement>(null)

  return (
    <div className='flex flex-col gap-3 w-full h-fit p-3'>
      <Input2
        placeholder='Search columns...'
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        leadingIcon='search'
        inputSize={'default'}
      />
      <ScrollArea
        viewportRef={viewPortRef}
        className='max-h-[420px] overflow-y-auto flex flex-col rounded-md'
      >
        {search &&
        filteredShown.length === 0 &&
        filteredHidden.length === 0 &&
        extraHidden.length === 0 ? (
          <SearchEmptyState />
        ) : (
          <>
            {filteredShown.length > 0 && (
              <>
                <div className='mt-6 mb-3 ml-2 text-xs text-grey-s4 font-semibold'>Shown</div>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={filteredShown} strategy={verticalListSortingStrategy}>
                    <div className='flex flex-col gap-1'>
                      {filteredShown.map((id: SpaceListColumn) => (
                        <SortableColumnRow
                          key={id}
                          id={id}
                          label={columnLabels[id]}
                          enabled={viewSettings.visible[id] ?? false}
                          onToggle={() => handleToggle(id)}
                          secondaryLabel={columnSecondaryLabels[id]}
                          searchTerm={search}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </>
            )}
            {(filteredHidden?.length > 0 || extraHidden?.length > 0) && (
              <>
                <div className='mt-4 ml-2 text-xs text-grey-s4 font-semibold'>Hidden</div>
                <div className='flex flex-col gap-1'>
                  {filteredHidden.map((id: SpaceListColumn) => (
                    <DraggableColumnRow
                      key={id}
                      label={columnLabels[id]}
                      enabled={viewSettings.visible[id] ?? false}
                      onToggle={() => handleToggle(id)}
                      grabDisabled
                      secondaryLabel={columnSecondaryLabels[id]}
                      searchTerm={search}
                    />
                  ))}
                  {extraHidden.map((id: SpaceListColumn) => (
                    <DraggableColumnRow
                      key={id}
                      label={columnLabels[id]}
                      enabled={viewSettings.visible[id] ?? false}
                      onToggle={() => handleToggle(id)}
                      grabDisabled
                      secondaryLabel={columnSecondaryLabels[id]}
                      searchTerm={search}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
        <ScrollAreaGradient scrollAreaRef={viewPortRef} />
      </ScrollArea>
    </div>
  )
}

const SearchEmptyState = () => (
  <EmptyState iconSize='small' className='p-3' iconClassName='text-blue-s3' icon='telescope'>
    <EmptyStateHeader>
      <EmptyStateTitle>Nothing found!</EmptyStateTitle>
    </EmptyStateHeader>
    <EmptyStateDescription>You may want to check for typos, try another word</EmptyStateDescription>
  </EmptyState>
)

export default SpaceListColumnSettingsPopover
